import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import calculateDistance from './calculateDistance';
import { updateDriverLocation } from '../DELIVERY/services/deliveryServices';
import { getDriverStatus, setDriverTrackingStatus } from '../../services/api/realtime';
import { useAuth } from '../USER/Auth/AuthContext';
import { TRACKING_STATUSES, DRIVER_STATUSES } from '../../constants';

const GPSContext = createContext();

const AUTO_SHUTDOWN_MS = 8 * 60 * 60 * 1000;
const GPS_PERMISSION_KEY = 'td_gps_permission_granted';

export const GPSProvider = ({ children }) => {
    const { currentUser, isDriver, isAdmin, isSuperAdmin } = useAuth();
    const [distanceToday, setDistanceToday] = useState(() => {
        const saved = localStorage.getItem('td_distance_today');
        return saved ? parseFloat(saved) : 0;
    });
    const [isEconomicMode, setIsEconomicMode] = useState(() => {
        return localStorage.getItem('td_economic_mode') === 'true';
    });
    const [lastLocation, setLastLocation] = useState(null);
    const [isTracking, setIsTracking] = useState(false);
    const [gpsOnline, setGpsOnlineState] = useState(false);
    const [gpsError, setGpsError] = useState(null);
    const [showPermissionModal, setShowPermissionModal] = useState(false);
    const watcherIdRef = useRef(null);
    const lastSyncTime = useRef(0);
    const shutdownTimerRef = useRef(null);
    const pendingActivationRef = useRef(false);
    const [spoofed, setSpoofed] = useState(false);
    const [degradedAccuracy, setDegradedAccuracy] = useState(false);
    const [spoofReason, setSpoofReason] = useState(null);
    const lastPositionRef = useRef(null);
    const spoofedRef = useRef(false);

    useEffect(() => {
        localStorage.setItem('td_distance_today', distanceToday.toString());
    }, [distanceToday]);

    useEffect(() => {
        localStorage.setItem('td_economic_mode', isEconomicMode.toString());
    }, [isEconomicMode]);

    const syncLocationToBackend = useCallback(async (lat, lng) => {
        if (!currentUser || spoofedRef.current) return;
        const now = Date.now();
        const syncInterval = isEconomicMode ? 30000 : 10000;
        if (now - lastSyncTime.current > syncInterval) {
            try {
                await updateDriverLocation(currentUser.uid || currentUser.id, lat, lng);
                lastSyncTime.current = now;
            } catch (err) {
                console.error("Error sincronizando ubicación:", err);
            }
        }
    }, [currentUser, isEconomicMode]);

    const stopTracking = useCallback(() => {
        if (watcherIdRef.current !== null) {
            navigator.geolocation.clearWatch(watcherIdRef.current);
            watcherIdRef.current = null;
        }
        setIsTracking(false);
    }, []);

    const startTracking = useCallback(() => {
        if (!navigator.geolocation) return;
        if (watcherIdRef.current !== null) {
            navigator.geolocation.clearWatch(watcherIdRef.current);
        }
        const id = navigator.geolocation.watchPosition(
            (position) => {
                const { latitude, longitude, accuracy } = position.coords;
                const timestamp = position.timestamp;

                if (lastPositionRef.current) {
                    const distanceKm = calculateDistance(
                        lastPositionRef.current.lat,
                        lastPositionRef.current.lng,
                        latitude,
                        longitude
                    );
                    const timeDeltaHours = (timestamp - lastPositionRef.current.timestamp) / 3600000;
                    const timeDeltaSeconds = (timestamp - lastPositionRef.current.timestamp) / 1000;
                    const speedKmh = timeDeltaHours > 0 ? distanceKm / timeDeltaHours : 0;

                    // Anti-Spoofing Rules:
                    // 1. Impossible speed (>250 km/h)
                    // 2. Instant teleportation (>0.5 km in < 5 seconds)
                    const isInstantTeleport = distanceKm > 0.5 && timeDeltaSeconds > 0 && timeDeltaSeconds < 5;

                    if (speedKmh > 250 || isInstantTeleport) {
                        const reason = speedKmh > 250 
                            ? 'Velocidad imposible detectada (>250 km/h)' 
                            : 'Salto geográfico instantáneo detectado (>0.5km en <5s)';

                        if (!spoofedRef.current) {
                            setSpoofed(true);
                            setSpoofReason(reason);
                            spoofedRef.current = true;
                        }
                        console.warn('[Anti-Spoofing GPS] Coordenada descartada:', { latitude, longitude, speedKmh, distanceKm, reason });
                        return;
                    } else if (spoofedRef.current) {
                        setSpoofed(false);
                        setSpoofReason(null);
                        spoofedRef.current = false;
                    }
                }

                if (accuracy > 100) {
                    setDegradedAccuracy(true);
                } else {
                    setDegradedAccuracy(false);
                }

                lastPositionRef.current = { lat: latitude, lng: longitude, timestamp };

                setLastLocation(prev => {
                    if (prev) {
                        const km = calculateDistance(prev.latitude, prev.longitude, latitude, longitude);
                        setDistanceToday(d => d + km);
                    }
                    return { latitude, longitude };
                });
                syncLocationToBackend(latitude, longitude);
            },
            (err) => {
                console.error("GPS Error:", err);
                if (err.code === 1) {
                    setGpsError('Permiso de ubicación denegado en tu navegador.');
                }
            },
            {
                enableHighAccuracy: !isEconomicMode,
                timeout: 15000,
                maximumAge: isEconomicMode ? 30000 : 5000
            }
        );
        watcherIdRef.current = id;
        setIsTracking(true);
    }, [isEconomicMode, syncLocationToBackend]);

    useEffect(() => {
        if (isTracking) {
            stopTracking();
            startTracking();
        }
    }, [isEconomicMode, startTracking, stopTracking]);

    const completeActivation = useCallback(async (initialCoords = null) => {
        pendingActivationRef.current = false;
        startTracking();

        const uid = currentUser?.uid || currentUser?.id;
        const lat = initialCoords?.latitude ?? lastLocation?.latitude;
        const lng = initialCoords?.longitude ?? lastLocation?.longitude;

        if (uid && lat != null && lng != null && !(Number(lat) === 0 && Number(lng) === 0)) {
            try {
                await updateDriverLocation(uid, Number(lat), Number(lng), TRACKING_STATUSES.ONLINE);
            } catch {}
        } else if (uid && navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (pos) => {
                    if (pos?.coords) {
                        const { latitude, longitude } = pos.coords;
                        setLastLocation({ latitude, longitude });
                        updateDriverLocation(uid, latitude, longitude, TRACKING_STATUSES.ONLINE).catch(() => {});
                    }
                },
                () => {},
                { enableHighAccuracy: false, timeout: 8000, maximumAge: 30000 }
            );
        }

        setGpsOnlineState(true);
        setGpsError(null);

        clearTimeout(shutdownTimerRef.current);
        shutdownTimerRef.current = setTimeout(() => {
            setGpsOnlineState(false);
        }, AUTO_SHUTDOWN_MS);
    }, [currentUser, lastLocation, startTracking]);

    const requestGpsPermission = useCallback(() => {
        if (!navigator.geolocation) {
            setGpsError('Tu dispositivo no soporta geolocalización.');
            setShowPermissionModal(false);
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (pos) => {
                localStorage.setItem(GPS_PERMISSION_KEY, 'true');
                setShowPermissionModal(false);
                const coords = pos?.coords ? { latitude: pos.coords.latitude, longitude: pos.coords.longitude } : null;
                if (coords) setLastLocation(coords);
                completeActivation(coords);
            },
            (err) => {
                if (err.code === 1) {
                    setShowPermissionModal(false);
                    setGpsError('Permiso de ubicación denegado. Actívalo en la configuración de tu navegador.');
                } else {
                    navigator.geolocation.getCurrentPosition(
                        (fallbackPos) => {
                            localStorage.setItem(GPS_PERMISSION_KEY, 'true');
                            setShowPermissionModal(false);
                            const coords = fallbackPos?.coords ? { latitude: fallbackPos.coords.latitude, longitude: fallbackPos.coords.longitude } : null;
                            if (coords) setLastLocation(coords);
                            completeActivation(coords);
                        },
                        () => {
                            localStorage.setItem(GPS_PERMISSION_KEY, 'true');
                            setShowPermissionModal(false);
                            completeActivation(null);
                        },
                        { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 }
                    );
                }
            },
            { enableHighAccuracy: true, timeout: 6000, maximumAge: 15000 }
        );
    }, [completeActivation]);

    const denyGpsPermission = useCallback(() => {
        setShowPermissionModal(false);
        setGpsOnlineState(false);
        pendingActivationRef.current = false;
    }, []);

    const setGpsOnline = useCallback(async (enabled) => {
        if (!currentUser) return;
        const uid = currentUser.uid || currentUser.id;

        if (enabled) {
            setGpsError(null);
            const userIsAdmin = Boolean(isAdmin || isSuperAdmin);

            // Si no es admin, verificar estado de conductor
            if (!userIsAdmin) {
                try {
                    const { data: driver } = await getDriverStatus(uid);
                    const isApproved = driver && (driver.status === DRIVER_STATUSES.ACTIVE || driver.is_approved === true);
                    if (!isApproved && !isDriver) {
                        setGpsError('Tu cuenta de conductor está pendiente de aprobación por administración.');
                        return;
                    }
                    if (driver && (driver.status === DRIVER_STATUSES.SUSPENDED || driver.status === DRIVER_STATUSES.REJECTED)) {
                        setGpsError(`Tu cuenta de conductor se encuentra ${driver.status}. Contacta a soporte.`);
                        return;
                    }
                } catch {
                    if (!isDriver) {
                        setGpsError('Error al verificar tu estado de conductor.');
                        return;
                    }
                }
            }

            if (!navigator.geolocation) {
                setGpsError('Tu navegador no soporta geolocalización.');
                return;
            }

            // Adquisición inmediata de posición (el toggle es una acción directa del usuario)
            navigator.geolocation.getCurrentPosition(
                (pos) => {
                    localStorage.setItem(GPS_PERMISSION_KEY, 'true');
                    setShowPermissionModal(false);
                    const coords = pos?.coords ? { latitude: pos.coords.latitude, longitude: pos.coords.longitude } : null;
                    if (coords) setLastLocation(coords);
                    completeActivation(coords);
                },
                (err) => {
                    if (err.code === 1) { // PERMISSION_DENIED
                        setGpsError('Permiso de ubicación denegado en tu navegador. Actívalo en la barra de direcciones o ajustes.');
                        setShowPermissionModal(false);
                        setGpsOnlineState(false);
                    } else {
                        // Timeout o POSITION_UNAVAILABLE: fallback a baja precisión
                        navigator.geolocation.getCurrentPosition(
                            (fallbackPos) => {
                                localStorage.setItem(GPS_PERMISSION_KEY, 'true');
                                setShowPermissionModal(false);
                                const coords = fallbackPos?.coords ? { latitude: fallbackPos.coords.latitude, longitude: fallbackPos.coords.longitude } : null;
                                if (coords) setLastLocation(coords);
                                completeActivation(coords);
                            },
                            () => {
                                // Aún sin coordenadas inmediatas, activar modo online para recibir actualizaciones del watcher
                                localStorage.setItem(GPS_PERMISSION_KEY, 'true');
                                setShowPermissionModal(false);
                                completeActivation(null);
                            },
                            { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 }
                        );
                    }
                },
                { enableHighAccuracy: true, timeout: 6000, maximumAge: 15000 }
            );
        } else {
            stopTracking();
            try {
                await setDriverTrackingStatus(uid, TRACKING_STATUSES.OFFLINE);
            } catch {}
            setGpsOnlineState(false);
            clearTimeout(shutdownTimerRef.current);
            shutdownTimerRef.current = null;
        }
    }, [currentUser, isAdmin, isSuperAdmin, isDriver, completeActivation, stopTracking]);

    const resetDistance = useCallback(() => {
        setDistanceToday(0);
        localStorage.setItem('td_distance_today', '0');
    }, []);

    const clearGpsError = useCallback(() => setGpsError(null), []);

    const clearSpoofAlert = useCallback(() => {
        setSpoofed(false);
        setSpoofReason(null);
        spoofedRef.current = false;
    }, []);

    const value = React.useMemo(() => ({
        distanceToday,
        isTracking,
        startTracking,
        stopTracking,
        resetDistance,
        lastLocation,
        isEconomicMode,
        setIsEconomicMode,
        gpsOnline,
        setGpsOnline,
        gpsError,
        clearGpsError,
        showPermissionModal,
        requestGpsPermission,
        denyGpsPermission,
        spoofed,
        degradedAccuracy,
        spoofReason,
        clearSpoofAlert,
    }), [
        distanceToday, isTracking, startTracking, stopTracking, resetDistance,
        lastLocation, isEconomicMode, setIsEconomicMode, gpsOnline, setGpsOnline,
        gpsError, clearGpsError, showPermissionModal, requestGpsPermission, denyGpsPermission,
        spoofed, degradedAccuracy, spoofReason, clearSpoofAlert
    ]);

    return (
        <GPSContext.Provider value={value}>
            {children}
        </GPSContext.Provider>
    );
};

export const useGlobalGPS = () => useContext(GPSContext);
